import React, { useState, useEffect } from 'react';
import {
  COLORS,
  SPACING,
  FONT_SIZE,
  FONT_WEIGHT,
  BORDER_RADIUS,
  SHADOWS,
  TOUCH_TARGET,
} from '../../constants/theme';
import { formatDate, isDrillOverdue } from '../../utils/formatters';
import {
  getStatusBadgeStyle,
  getCategoryBadgeStyle,
  getDifficultyBadgeStyle,
} from '../../utils/badgeStyles';
import { supabase } from '../../lib/supabaseClient';

/**
 * DrillCard Component
 * Mobile-optimized card for displaying assigned drill information
 *
 * @param {Object} assignment - The drill assignment object
 * @param {Function} onViewDetails - Callback when "View Details" is clicked
 * @param {Function} onMarkComplete - Callback when "Mark Complete" is clicked
 */
const DrillCard = ({ assignment, onViewDetails, onMarkComplete }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [thumbnailLoading, setThumbnailLoading] = useState(false);

  // Load thumbnail URL (only for uploaded videos with auto-extracted thumbnails)
  useEffect(() => {
    const loadThumbnail = async () => {
      const drill = assignment?.drills;
      if (!drill) return;

      setThumbnailLoading(true);

      try {
        // Only load thumbnail if it was auto-extracted from uploaded video
        if (drill.thumbnail_source === 'video_frame' && drill.thumbnail_storage_path) {
          const { data, error } = await supabase.storage
            .from('drill-thumbnails')
            .createSignedUrl(drill.thumbnail_storage_path, 31536000); // 1 year

          if (!error && data?.signedUrl) {
            setThumbnailUrl(data.signedUrl);
          }
        }
        // For URL-based videos, don't load any thumbnail (will show play button)
      } catch (err) {
        console.error('Error loading thumbnail:', err);
      } finally {
        setThumbnailLoading(false);
      }
    };

    loadThumbnail();
  }, [assignment]);

  // Check if drill has any video
  const hasVideo = () => {
    const drill = assignment?.drills;
    return drill?.video_url || drill?.video_storage_path;
  };
  const styles = {
    card: {
      backgroundColor: COLORS.white,
      borderRadius: BORDER_RADIUS.md,
      boxShadow: SHADOWS.md,
      padding: SPACING.lg,
      marginBottom: SPACING.md,
      border: `1px solid ${COLORS.gray[300]}`,
    },
    thumbnail: {
      width: '100%',
      height: '160px',
      objectFit: 'cover',
      borderRadius: BORDER_RADIUS.sm,
      marginBottom: SPACING.md,
      backgroundColor: COLORS.gray[200],
    },
    thumbnailPlaceholder: {
      width: '100%',
      height: '160px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: COLORS.gray[100],
      borderRadius: BORDER_RADIUS.sm,
      marginBottom: SPACING.md,
      fontSize: FONT_SIZE['2xl'],
      color: COLORS.gray[400],
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: SPACING.md,
      paddingBottom: SPACING.md,
      borderBottom: `1px solid ${COLORS.gray[200]}`,
    },
    title: {
      fontSize: FONT_SIZE.lg,
      fontWeight: FONT_WEIGHT.semibold,
      color: COLORS.gray[700],
      flex: 1,
      marginRight: SPACING.sm,
    },
    body: {
      display: 'flex',
      flexDirection: 'column',
      gap: SPACING.sm,
      marginBottom: SPACING.md,
      fontSize: FONT_SIZE.base,
    },
    row: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: SPACING.xs,
    },
    label: {
      fontWeight: FONT_WEIGHT.semibold,
      color: COLORS.gray[600],
      fontSize: FONT_SIZE.md,
    },
    value: {
      color: COLORS.gray[700],
      fontSize: FONT_SIZE.md,
    },
    overdueText: {
      color: COLORS.danger,
      fontWeight: FONT_WEIGHT.semibold,
    },
    actions: {
      display: 'flex',
      gap: SPACING.sm,
      justifyContent: 'stretch',
    },
    button: {
      flex: 1,
      padding: SPACING.md,
      fontSize: FONT_SIZE.base,
      fontWeight: FONT_WEIGHT.medium,
      border: 'none',
      borderRadius: BORDER_RADIUS.sm,
      cursor: 'pointer',
      textAlign: 'center',
      minHeight: TOUCH_TARGET.minimum,
    },
    primaryButton: {
      backgroundColor: COLORS.primary,
      color: COLORS.white,
    },
    successButton: {
      backgroundColor: COLORS.success,
      color: COLORS.white,
    },
  };

  return (
    <div style={styles.card}>
      {/* Thumbnail - only show if drill has a video */}
      {hasVideo() && (
        <>
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={assignment.drills?.name || 'Drill thumbnail'}
              style={styles.thumbnail}
            />
          ) : (
            !thumbnailLoading && <div style={styles.thumbnailPlaceholder}>🎬</div>
          )}
        </>
      )}

      <div style={styles.header}>
        <div style={styles.title}>{assignment.drills?.name || 'N/A'}</div>
        <span style={getStatusBadgeStyle(assignment.completed)}>
          {assignment.completed ? 'Completed' : 'Pending'}
        </span>
      </div>

      <div style={styles.body}>
        <div style={styles.row}>
          <span style={getCategoryBadgeStyle(assignment.drills?.category)}>
            {assignment.drills?.category || 'N/A'}
          </span>
          <span style={getDifficultyBadgeStyle(assignment.drills?.difficulty_level)}>
            {assignment.drills?.difficulty_level || 'N/A'}
          </span>
        </div>

        <div style={styles.row}>
          <div>
            <div style={styles.label}>Assigned</div>
            <div style={styles.value}>{formatDate(assignment.assigned_date)}</div>
          </div>
          <div>
            <div style={styles.label}>Due Date</div>
            <div
              style={{
                ...styles.value,
                ...(isDrillOverdue(assignment.due_date, assignment.completed) &&
                  styles.overdueText),
              }}
            >
              {formatDate(assignment.due_date)}
              {isDrillOverdue(assignment.due_date, assignment.completed) && ' (Overdue)'}
            </div>
          </div>
        </div>

        {assignment.coach_notes && (
          <div>
            <div style={styles.label}>Coach Notes</div>
            <div style={styles.value}>
              {assignment.coach_notes.length > 80
                ? `${assignment.coach_notes.substring(0, 80)}...`
                : assignment.coach_notes}
            </div>
          </div>
        )}
      </div>

      <div style={styles.actions}>
        <button
          style={{ ...styles.button, ...styles.primaryButton }}
          onClick={() => onViewDetails(assignment)}
        >
          View Details
        </button>
        {!assignment.completed && (
          <button
            style={{ ...styles.button, ...styles.successButton }}
            onClick={() => onMarkComplete(assignment)}
          >
            Mark Complete
          </button>
        )}
      </div>
    </div>
  );
};

export default DrillCard;
